/**
 * Tournament Strategy
 **/

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TournamentPanel } from '../tournaments';
import CampaignGamesGrid from '../../MultiViewCampaignSection/components/CampaignGamesGrid';
import { CampaignSectionFallback } from '../../MultiViewCampaignSection/components';
import MultiCampaignsGrid from '../../defaults/MultiCampaignGrid/MultiCampaignGrid';

import {
  useItemsByCampaignType,
  TOURNAMENTS_CAMPAIGN_TYPE,
} from 'features/campaigns';
import { useCampaignSectionBase } from '../../MultiViewCampaignSection/_hooks/useCampaignSection';
import { useCampaignPanelInfo } from '../../MultiViewCampaignSection/_hooks/useCampaignPanelInfo';
import type {
  CampaignSectionStrategy,
  CampaignSectionState,
} from '../../MultiViewCampaignSection/types';
import type ISectionSettings from 'types/models/ISectionSettings';
import type { CampaignSource } from '../../../types/campaign.types';

/** Hook implementation for tournament section state */
const useTournamentSectionState = (
  data: ISectionSettings,
  isActive: boolean,
): CampaignSectionState<CampaignSource> => {
  const { t } = useTranslation();
  const base = useCampaignSectionBase(data, isActive);

  const { items: tournaments } = useItemsByCampaignType(TOURNAMENTS_CAMPAIGN_TYPE) as {
    items: CampaignSource[];
    hasMany: boolean;
    hasItems: boolean;
  };

  const isLoading = false;
  const isError = false;
  const error = null;

  const handleRetry = useCallback(() => {
    console.log('[TournamentStrategy] Retry tournaments');
  }, []);

  const isSingleItem = tournaments.length === 1;
  const activeCampaign = isSingleItem ? tournaments[0] : null;

  const containerProps = base.createContainerProps({
    'data-one-tournament': isSingleItem ? 'true' : 'false',
  });

  const panelInfo = useCampaignPanelInfo({
    campaignType: TOURNAMENTS_CAMPAIGN_TYPE,
    data,
    items: tournaments,
  });

  return {
    t,
    viewMode: base.viewMode,
    isLoading,
    isError,
    error,
    panelInfo,
    items: tournaments,
    isSingleItem,
    activeCampaign,
    handleViewToggle: base.handleViewToggle,
    handleRetry,
    handleNavigateToLobby: base.handleNavigateToLobby,
    containerProps,
    itemCount: tournaments.length,
  };
};

/** Tournament Strategy Implementation */
export const tournamentStrategy: CampaignSectionStrategy<CampaignSource> = {
  type: TOURNAMENTS_CAMPAIGN_TYPE,

  useCampaignSection: useTournamentSectionState,

  renderMultiItemsLayout: ({ items, name, isActive }) => (
    <MultiCampaignsGrid name={name} campaigns={items} isActive={isActive} />
  ),

  renderSingleItemLayout: ({ item, data, isActive }) => (
    <CampaignGamesGrid data={data} campaign={item} isActive={isActive} />
  ),

  renderPanel: (panelInfo) => <TournamentPanel panelInfo={panelInfo} />,

  renderFallback: ({ sectionName, error, onRetry, viewMode }) => (
    <CampaignSectionFallback
      sectionName={sectionName}
      error={error}
      onRetry={onRetry}
      viewMode={viewMode}
      sectionType="tournaments"
    />
  ),
};

// probably we don't need export const tournamentStrategy and export default tournamentStrategy
// fallow-ignore-next-line unused-export
export default tournamentStrategy;
